import { Button, Text } from '@/components/ui';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select, SelectOption } from '@/components/ui/Select';
import { BriefcaseIcon, ChartColumnIcon, FileDownIcon, GaugeIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useEffect } from 'react';
import { DateRange } from 'react-day-picker';
import { useReports } from '@/hooks/useReports';
import { useContractOptions } from '@/hooks/useContractOptions';
import { useCompanies } from '@/hooks/useCompanies';
import { Loader } from '@/components/Loader';
import { toast } from 'sonner';
import { useSyncStore } from '@/lib/sync';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function ReportsPage() {
  const [ticketsContractId, setTicketsContractId] = useState<string>('');
  const [ticketsCompanyId, setTicketsCompanyId] = useState<string>('all');
  const [ticketsDateRange, setTicketsDateRange] = useState<DateRange | undefined>();
  const [contractsCompanyId, setContractsCompanyId] = useState<string>('all');
  const [monthlyContractId, setMonthlyContractId] = useState<string>('all');
  const [monthlyCompanyId, setMonthlyCompanyId] = useState<string>('all');
  const [monthlyYear, setMonthlyYear] = useState<string>('all');
  const [monthlyMonth, setMonthlyMonth] = useState<string>('all');
  const { lastTicketsSync } = useSyncStore();

  const { t } = useTranslation();
  const {
    isGenerating,
    generateTicketsReport,
    generateContractsReport,
    generateMonthlyConsumptionReport,
  } = useReports();

  // Separate contract options for each report
  const { contractOptions: ticketsContractOptions } = useContractOptions({
    includeInactive: true,
    companyId: ticketsCompanyId,
  });
  const { contractOptions: monthlyContractOptions } = useContractOptions({
    includeInactive: true,
    includeAll: true,
    companyId: monthlyCompanyId,
  });

  const { companies } = useCompanies();

  const companyOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', content: t('common.all') },
      ...companies.map((company) => ({
        value: company.id,
        content: company.name,
      })),
    ],
    [companies, t]
  );

  const yearOptions: SelectOption[] = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push({ value: i.toString(), content: i.toString() });
    }
    return [{ value: 'all', content: t('common.all') }, ...years];
  }, [t]);

  const monthOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', content: t('common.all') },
      { value: '1', content: t('common.months.january') },
      { value: '2', content: t('common.months.february') },
      { value: '3', content: t('common.months.march') },
      { value: '4', content: t('common.months.april') },
      { value: '5', content: t('common.months.may') },
      { value: '6', content: t('common.months.june') },
      { value: '7', content: t('common.months.july') },
      { value: '8', content: t('common.months.august') },
      { value: '9', content: t('common.months.september') },
      { value: '10', content: t('common.months.october') },
      { value: '11', content: t('common.months.november') },
      { value: '12', content: t('common.months.december') },
    ],
    [t]
  );

  // Report generation handlers
  const handleGenerateTicketsReport = async () => {
    // Validate required fields
    if (!ticketsContractId) {
      toast.error(t('reports.tickets.contractRequired'));
      return;
    }
    if (!ticketsDateRange?.from || !ticketsDateRange?.to) {
      toast.error(t('reports.tickets.dateRangeRequired'));
      return;
    }

    const filters = {
      contractId: ticketsContractId,
      companyId: ticketsCompanyId === 'all' ? undefined : ticketsCompanyId,
      startDate: ticketsDateRange.from.toISOString().split('T')[0],
      endDate: ticketsDateRange.to.toISOString().split('T')[0],
    };
    await generateTicketsReport(filters);
  };

  const handleGenerateContractsReport = async () => {
    const filters = {
      companyId: contractsCompanyId === 'all' ? undefined : contractsCompanyId,
    };
    await generateContractsReport(filters);
  };

  const handleGenerateMonthlyReport = async () => {
    const filters = {
      contractId: monthlyContractId === 'all' ? undefined : monthlyContractId,
      companyId: monthlyCompanyId === 'all' ? undefined : monthlyCompanyId,
      year: monthlyYear === 'all' ? undefined : monthlyYear,
      month: monthlyMonth === 'all' ? undefined : monthlyMonth,
    };
    await generateMonthlyConsumptionReport(filters);
  };

  // Reset contract selection when company changes
  useEffect(() => {
    setTicketsContractId('');
  }, [ticketsCompanyId]);

  useEffect(() => {
    setMonthlyContractId('all');
  }, [monthlyCompanyId]);

  return (
    <div className="gap-2xl flex flex-1 flex-col">
      <Loader isLoading={isGenerating} />
      <header className="flex w-full items-center justify-between">
        <Text as="h1" variant="heading">
          {t('reports.title')}
        </Text>
        {lastTicketsSync && (
          <Text variant="label" size="sm" className="text-neutral-muted">
            {t('reports.lastTicketsSync')}{' '}
            {formatDistanceToNow(new Date(lastTicketsSync), { addSuffix: true, locale: ptBR })}
          </Text>
        )}
      </header>

      <Link
        to="/reports/contract-portfolio"
        className="border-transparent-dark-12 bg-base-white p-md hover:border-brand-default flex items-center justify-between rounded-2xl border shadow-xs transition-colors"
      >
        <div>
          <Text size="md" strong>
            {t('reports.contractPortfolio.title')}
          </Text>
          <Text className="text-neutral-muted">{t('reports.contractPortfolio.description')}</Text>
        </div>
        <BriefcaseIcon size={24} className="text-brand-emphasis" />
      </Link>

      <Link
        to="/reports/resource-allocation"
        className="border-transparent-dark-12 bg-base-white p-md hover:border-brand-default flex items-center justify-between rounded-2xl border shadow-xs transition-colors"
      >
        <div>
          <Text size="md" strong>
            {t('reports.resourceAllocation.title')}
          </Text>
          <Text className="text-neutral-muted">{t('reports.resourceAllocation.description')}</Text>
        </div>
        <GaugeIcon size={24} className="text-brand-emphasis" />
      </Link>

      <Link
        to="/reports/contract-consumption"
        className="border-transparent-dark-12 bg-base-white p-md hover:border-brand-default flex items-center justify-between rounded-2xl border shadow-xs transition-colors"
      >
        <div>
          <Text size="md" strong>
            {t('reports.contractConsumption.title')}
          </Text>
          <Text className="text-neutral-muted">{t('reports.contractConsumption.description')}</Text>
        </div>
        <ChartColumnIcon size={24} className="text-brand-emphasis" />
      </Link>

      <section className="border-transparent-dark-12 bg-base-white p-md gap-sm flex flex-col rounded-2xl border shadow-xs">
        <div className="gap-xs flex">
          <Select
            options={companyOptions}
            placeholder={t('reports.filters.companyPlaceholder')}
            width={276}
            value={ticketsCompanyId === 'all' ? '' : ticketsCompanyId}
            onValueChange={(value) => setTicketsCompanyId(value || 'all')}
          />
          <Select
            options={ticketsContractOptions}
            placeholder={t('reports.filters.contractPlaceholder')}
            width={276}
            value={ticketsContractId}
            onValueChange={(value) => setTicketsContractId(value || '')}
          />
          <DatePicker
            placeholder={t('reports.filters.periodPlaceholder')}
            mode="range"
            width={276}
            value={ticketsDateRange}
            onChange={(date) => setTicketsDateRange(date as DateRange | undefined)}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Text size="md" strong>
              {t('reports.tickets.title')}
            </Text>
            <Text className="text-neutral-muted">{t('reports.tickets.description')}</Text>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleGenerateTicketsReport}
            disabled={
              isGenerating || !ticketsContractId || !ticketsDateRange?.from || !ticketsDateRange?.to
            }
            loading={isGenerating}
          >
            <FileDownIcon size={20} />
            PDF
          </Button>
        </div>
      </section>

      <section className="border-transparent-dark-12 bg-base-white p-md gap-sm flex flex-col rounded-2xl border shadow-xs">
        <div className="gap-xs flex">
          <Select
            options={companyOptions}
            placeholder={t('reports.filters.companyPlaceholder')}
            width={276}
            value={contractsCompanyId === 'all' ? '' : contractsCompanyId}
            onValueChange={(value) => setContractsCompanyId(value || 'all')}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Text size="md" strong>
              {t('reports.contracts.title')}
            </Text>
            <Text className="text-neutral-muted">{t('reports.contracts.description')}</Text>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleGenerateContractsReport}
            disabled={isGenerating}
            loading={isGenerating}
          >
            <FileDownIcon size={20} />
            PDF
          </Button>
        </div>
      </section>

      <section className="border-transparent-dark-12 bg-base-white p-md gap-sm flex flex-col rounded-2xl border shadow-xs">
        <div className="gap-xs flex">
          <Select
            options={companyOptions}
            placeholder={t('reports.filters.companyPlaceholder')}
            width={276}
            value={monthlyCompanyId === 'all' ? '' : monthlyCompanyId}
            onValueChange={(value) => setMonthlyCompanyId(value || 'all')}
          />
          <Select
            options={monthlyContractOptions}
            placeholder={t('reports.filters.contractPlaceholder')}
            width={276}
            value={monthlyContractId === 'all' ? '' : monthlyContractId}
            onValueChange={(value) => setMonthlyContractId(value || 'all')}
          />
          <Select
            options={yearOptions}
            placeholder={t('reports.filters.yearPlaceholder')}
            width={276}
            value={monthlyYear === 'all' ? '' : monthlyYear}
            onValueChange={(value) => setMonthlyYear(value || 'all')}
          />
          <Select
            options={monthOptions}
            placeholder={t('reports.filters.monthPlaceholder')}
            width={276}
            value={monthlyMonth === 'all' ? '' : monthlyMonth}
            onValueChange={(value) => setMonthlyMonth(value || 'all')}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Text size="md" strong>
              {t('reports.monthlyConsumption.title')}
            </Text>
            <Text className="text-neutral-muted">
              {t('reports.monthlyConsumption.description')}
            </Text>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleGenerateMonthlyReport}
            disabled={isGenerating}
            loading={isGenerating}
          >
            <FileDownIcon size={20} />
            PDF
          </Button>
        </div>
      </section>
    </div>
  );
}
